import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';

import FileJSONIcon from '../../assets/icons/FileJSON';
import FileJSONGrayIcon from '../../assets/icons/FileJSONGray';
import { IRequestJsonDownloadUrl, useGetRequestJsonDownloadUrl } from '../../hooks/api/request-pcf';
import { useAuth } from '../../hooks/auth/useAuth';
import useDialogContext from '../../hooks/dialog/useDialogContext';
import useNotificationContext from '../../hooks/notification/useNotificationContext';
import { Locale } from '../../i18n';
import { SUPPORT_EMAIL } from '../../shared/constants/emails.constant';
import { ToastSeverity } from '../../shared/enums/toast-severity';
import generateEmailBody from './generateEmailBody';

export type DownloadJsonFileButtonProps = {
  className?: string;
  componentName?: string;
  componentId?: string;
  requestId: number;
};

const DownloadJsonFileButton = ({
  className,
  componentName,
  componentId,
  requestId,
}: DownloadJsonFileButtonProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { user, isCompanyAdmin } = useAuth();
  const { openDialog } = useDialogContext();
  const { notify } = useNotificationContext();
  const queryClient = useQueryClient();

  const isDisabled = user?.company?.license === 'basic';

  const { refetch: fetchJsonDownload } = useGetRequestJsonDownloadUrl(requestId, {
    retry: false,
  });

  const downloadJsonFile = (jsonData: IRequestJsonDownloadUrl) => {
    const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(jsonData),
    )}`;
    const link = document.createElement('a');
    link.href = jsonString;
    link.download = `${(componentName || componentId || 'component_pcf')
      .toLowerCase()
      .replace(/ /g, '_')}_pcf.json`;

    link.click();

    // Invalidate components list since opening JSON file will update the status to completed
    queryClient.invalidateQueries({ queryKey: ['components'] });
  };

  const fetchAndDownloadJsonFile = async () => {
    const { data, isSuccess } = await fetchJsonDownload();

    if (!isSuccess || !data) {
      return;
    }

    // The provider withholds the PCF until its data usage terms are accepted,
    // so the download is resumed once that has happened.
    if (data.termsAcceptanceRequired) {
      // Only a company admin can accept the provider's terms on behalf of the
      // whole company.
      if (!isCompanyAdmin) {
        notify({
          severity: ToastSeverity.WARN,
          detail: t('toastMessages.dataUsageTermsAcceptancePending', {
            providerCompanyName: data.providerCompanyName,
          }),
          life: 8000,
        });

        return;
      }

      openDialog('accept-data-usage-terms-dialog', {
        productId: String(data.productId),
        onAccept: fetchAndDownloadJsonFile,
      });

      return;
    }

    downloadJsonFile(data);
  };

  const handleClick = async () => {
    if (isDisabled) {
      return;
    }

    await fetchAndDownloadJsonFile();
  };

  const handleEmailClick = () => {
    const emailSubject = t('emails.upgradeInquiry.subject');
    let emailBody = generateEmailBody(user, language as Locale);

    // Encode the email body for the mailto link
    emailBody = encodeURIComponent(emailBody);

    // Create the mailto link with the recipient, subject, and body
    const mailtoLink = `mailto:${SUPPORT_EMAIL}?subject=${emailSubject}&body=${emailBody}`;

    // Open the user's default email client with the mailto link
    window.location.href = mailtoLink;
  };

  return (
    <div>
      {isDisabled && (
        <Tooltip
          target={`.json-download-btn-${requestId}`}
          position='top'
          autoHide={false}
          baseZIndex={1}
          pt={{ root: { className: 'pointer-events-auto' } }}
        >
          <div>
            <span>{t('componentsPage.jsonDownloadDisabledTooltip.part1')}</span>{' '}
            <Button
              link
              label={t('componentsPage.jsonDownloadDisabledTooltip.clickHere')}
              className='p-0'
              onClick={handleEmailClick}
            />{' '}
            <span>{t('componentsPage.jsonDownloadDisabledTooltip.part2')}</span>
          </div>
        </Tooltip>
      )}
      <Button
        className={classNames(
          `p-0 json-download-btn-${requestId}`,
          {
            'cursor-pointer': !isDisabled,
            'cursor-auto': isDisabled,
          },
          className,
        )}
        icon={isDisabled ? FileJSONGrayIcon : FileJSONIcon}
        link
        onClick={handleClick}
      />
    </div>
  );
};

export default DownloadJsonFileButton;
